import React, { memo } from "react";
import { formatNumber } from "@";

/**
 * Refined hero header for listing/table pages.
 * Renders a decorative orb, an accent bar, an uppercase eyebrow label,
 * the page title, an optional live count chip, and a slot for right-side options.
 *
 * Use inside `<main className="table-card relative ">` so the
 * decorative orb anchors correctly.
 *
 * @example
 *   <PageHero
 *     eyebrow="العمليات / الجودة"
 *     title="إدارة الجودة"
 *     count={totalItems}
 *     icon={ShieldCheckIcon}
 *     hideCount={isLoading}
 *   >
 *     <TableRowsPerPage ... />
 *     <SearchToggle ... />
 *   </PageHero>
 */
const PageHero = ({
  title,
  eyebrow,
  count,
  icon: Icon,
  hideCount = false,
  decorative = true,
  headerClassName = "",
  children,
}) => {
  const showCount =
    !hideCount && count !== undefined && count !== null && count !== "";

  return (
    <div className="relative">
      {decorative && (
        <span
          aria-hidden
          className="pointer-events-none absolute end-0 top-0 h-56 w-56 rounded-full bg-gradient-to-br from-purple-200/30 to-transparent blur-3xl dark:from-purple-700/20"
        />
      )}

      <header
        className={`flex-container relative gap-3 md:gap-2 ${headerClassName}`}
      >
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="hidden h-12 w-1 rounded-full bg-gradient-to-b from-purple-700 to-purple-950 dark:from-purple-400 dark:to-purple-700 md:block"
          />
          <div>
            {eyebrow && (
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 dark:text-purple-400">
                {eyebrow}
              </p>
            )}
            <h1 className="page-title flex items-center gap-2.5">
              <span>{title}</span>
              {showCount && (
                <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-bold tabular-nums text-purple-900 ring-1 ring-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40">
                  {Icon && <Icon className="h-3.5 w-3.5" />}
                  {typeof count === "number" ? formatNumber(count) : count}
                </span>
              )}
            </h1>
          </div>
        </div>

        {children && <div className="options-container">{children}</div>}
      </header>
    </div>
  );
};

export default memo(PageHero);
